// combatpro/views/MoraleView.js
//
// Stage 33: view-morale shell. Stage 44 (Morale Runtime) wires it: the empty
// vi-morale-mount (which the Stage 30 moraleItems placeholder layer never
// populated) now renders the morale card for the ACTIVE monster
// (state.currentCombatantIndex), reproducing the legacy per-monster morale card
// (battletracker.html 5954-6000) - morale bar/value/status, retreat threshold,
// leader flag, and manual adjustment.
//
// The legacy built one morale-block-${i} per monster inside createMonsterPanel
// and reparented the active one into vi-morale-mount via vi_mountMorale (10195);
// React renders the active monster's card directly (no reparenting). Status tier
// (bar colour/text + status colour/bg/text) comes from the pure morale engine;
// threshold / leader / manual-adjust write through the Stage 44 context handlers.
// Combat log, saveState, and the per-monster panel copy are deferred. No DOM
// manipulation, no getElementById. No optional chaining. No em dash.

import React, { useContext, useState } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { getMoraleStatus } from '../morale/moraleEngine';

function MoraleView() {
  const { state, updateMoraleThreshold, updateLeader, adjustMorale } = useContext(CombatContext);
  const index = state.currentCombatantIndex;
  const monster = state.monsters[index];

  // Local input value for the manual adjustment field (legacy moraleAdjust-${i});
  // cleared after Apply, matching adjustMorale's input reset (9753).
  const [adjustValue, setAdjustValue] = useState('');

  if (!monster) {
    return (
      <div className="vi-view" id="view-morale">
        <div id="vi-morale-mount"></div>
      </div>
    );
  }

  const morale = monster.morale;
  const threshold = monster.moraleThreshold;
  const status = getMoraleStatus(morale, threshold);

  function onThresholdChange(raw) {
    const parsed = parseInt(raw, 10);
    updateMoraleThreshold(index, isNaN(parsed) ? 0 : parsed);
  }

  function applyAdjust() {
    const parsed = parseInt(adjustValue, 10);
    adjustMorale(index, isNaN(parsed) ? 0 : parsed);
    setAdjustValue('');
  }

  return (
    <div className="vi-view" id="view-morale">
      <div id="vi-morale-mount">
        <div className="vi-section-body" id={'morale-block-' + index}>
          <div className="vi-mb-20">
            <div className="vi-morale-value-row">
              <label className="vi-bold-gold">Morale</label>
              <span id={'moraleValue-' + index} className="vi-bold-gold">{morale + '%'}</span>
            </div>
            <div className="vi-morale-bar-track">
              <div id={'moraleBar-' + index} className={'vi-morale-bar ' + status.barClass} style={{ width: morale + '%', background: status.barColor }}>
                {status.barText}
              </div>
            </div>
            <div id={'moraleStatus-' + index} className="vi-morale-status" style={{ color: status.statusColor, background: status.statusBg, borderColor: status.statusColor }}>
              {status.statusText}
            </div>
          </div>

          <div className="vi-morale-cards">
            <div className="vi-morale-card">
              <label className="vi-label-sm">Retreat Threshold</label>
              <div className="vi-flex-center vi-gap-10">
                <input type="number" id={'moraleThreshold-' + index} value={threshold} min="0" max="100" onChange={event => onThresholdChange(event.target.value)} className="vi-threshold-input" />
                <span className="vi-text-readable">%</span>
              </div>
              <div className="vi-morale-hint">Monster flees when morale drops below this %</div>
            </div>
            <div className="vi-morale-card">
              <label className="vi-label-sm">Leadership</label>
              <label className="vi-leader-label">
                <input type="checkbox" id={'isLeader-' + index} checked={monster.isLeader} onChange={event => updateLeader(index, event.target.checked)} className="vi-checkbox-lg" />
                <span className="vi-bold-gold">👑 Leader</span>
              </label>
              <div className="vi-morale-hint">Leader death {'\u2192'} applies morale penalty to group</div>
            </div>
            <div className="vi-morale-card">
              <label className="vi-label-sm">Manual Morale Adjustment</label>
              <div className="vi-morale-adjust">
                <input type="number" id={'moraleAdjust-' + index} placeholder="±10" min="-100" max="100" className="vi-morale-adjust-input" value={adjustValue} onChange={event => setAdjustValue(event.target.value)} />
                <button onClick={applyAdjust} className="vi-morale-apply-btn">Apply</button>
              </div>
              <div className="vi-morale-hint">{'Rallying cry \u2192 +10\u00A0\u00B7\u00A0Fear effect \u2192 -20\u00A0\u00B7\u00A0Apply any adjustment manually'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MoraleView;
